import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { api, inr, fmtDate } from "../lib/api";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);

  useEffect(() => {
    if (!order) api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => {});
  }, [orderId, order]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="order-success-page">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="w-20 h-20 mx-auto rounded-full bg-[#78866B]/15 grid place-items-center">
        <CheckCircle2 size={40} className="text-[#5c6b52]" />
      </motion.div>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-6">Order placed successfully!</h1>
      <p className="text-stone-500 mt-3">Your gift is on its way. We've sent the details to your email too.</p>

      {order && (
        <div className="mt-8 p-6 rounded-2xl bg-white border border-[#E9E2D7] text-left" data-testid="order-success-summary">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider font-bold">Order ID</p>
              <p className="font-mono font-bold text-stone-900 mt-0.5" data-testid="order-success-id">{order.order_id}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-stone-400 uppercase tracking-wider font-bold">Total</p>
              <p className="font-bold text-[#B45309] text-lg">{inr(order.total)}</p>
            </div>
          </div>
          <div className="border-t border-stone-100 mt-4 pt-4 space-y-2">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                {i.image && <img src={i.image} alt={i.name} className="w-10 h-10 rounded-lg object-cover" />}
                <span className="flex-1 text-stone-700 line-clamp-1">{i.name} × {i.qty}</span>
                <span className="font-semibold text-stone-900">{inr(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>
          <p className="flex items-center gap-2 text-xs text-stone-500 mt-4"><Package size={13} className="text-[#B45309]" /> Estimated delivery: <b>{fmtDate(order.estimated_delivery)}</b> · Payment: {order.payment_method.toUpperCase()} ({order.payment_status === "paid" ? "Paid" : "Pay on delivery"})</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
        <Link to={`/orders/${orderId}`} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors" data-testid="track-order-cta">
          Track Order <ArrowRight size={15} />
        </Link>
        <Link to="/shop" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border-2 border-stone-900 text-stone-900 text-sm font-bold hover:bg-stone-900 hover:text-white transition-colors" data-testid="continue-shopping-cta">
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
